/**
 * Type guard for `.filter()` that drops null/undefined (and other falsy) entries and
 * narrows the resulting array element type accordingly.
 */
export const isTruthy = <T>(value: T | null | undefined): value is T => !!value

export const roundSafe = (input: number | null | undefined) => {
  if (input && !isNaN(input)) return Math.round(input)
  return undefined
}

/**
 * Deduplicates an array based on a provided function. Preserves the first occurrence of each unique item.
 * @param arr The array to deduplicate.
 * @param fn A function that returns a key to determine uniqueness.
 * @returns A new array with duplicates removed based on the key function.
 * @example
 * const fruits = ['apple', 'BANANA', 'Apple', 'pear', 'banana'];
 * const uniqueFruits = deduplicateBy(fruits, fruit => fruit.toLowerCase());
 * console.log(uniqueFruits); // Output: ['apple', 'BANANA', 'pear']
 */
export const deduplicateBy = <T, S>(arr: T[], fn: (item: T) => S): T[] => {
  const seen = new Set<S>()
  return arr.filter((item) => {
    const key = fn(item)
    if (seen.has(key)) {
      return false
    } else {
      seen.add(key)
      return true
    }
  })
}
